import { TickLayerName } from "../config/tickLayers";
import { Persistence } from "../config/constants";
import { BaseEntity } from "../core/entity/BaseEntity";
import { Entity } from "../core/entity/Entity";
import { on } from "../core/entity/handler";
import { setMoveInput, standAtStance } from "../sim/commands/player-commands";
import { Player } from "../sim/entities/Player";
import { StoryTracker } from "../sim/singletons/StoryTracker";
import { BenchDive } from "../shell/scenes/bench/BenchDive";
import { TripTheater } from "../shell/scenes/TripTheater";
import { ShellStore } from "../shell/ShellStore";
import { TargetingState } from "../shell/dispatch/TargetingState";
import { workStance } from "../game/player-motion";

/**
 * Feeds held movement keys (WASD/arrows/stick) into the player's move
 * input every engine tick — the walking half of input, needed for the
 * phase-3 walkable-shop gate. The full ShortcutDispatcher (operate,
 * wait, interaction keys, scopes) is phase 4.
 */
export class MovementInput extends BaseEntity implements Entity {
  persistenceLevel: number = Persistence.Permanent;
  tickLayer: TickLayerName = "input";

  @on("tick")
  onTick() {
    const player = this.game.entities.tryGetSingleton(Player);
    if (!player) return;
    // An open dialog owns the keyboard: the body stands still under it.
    // So does a story scene, box up or not — she's walking in. And a
    // player still shut in the truck as it backs up the driveway can't
    // walk anywhere: the sim has them home, but the body isn't out of
    // the cab yet, and steering an invisible one would carry it off
    // the truck before the door opens.
    if (
      this.game.entities.tryGetSingleton(ShellStore)?.modalOpen ||
      this.game.entities.tryGetSingleton(StoryTracker)?.playing != null ||
      this.game.entities.tryGetSingleton(TripTheater)?.playerAboard()
    ) {
      setMoveInput(this.game, [0, 0]);
      return;
    }
    // Leaned over a bench the walk keys are dead, and the body steps
    // into the working stance instead: centered on the bench's
    // operation cell, squared up to the top, so the zoomed-in look
    // finds the woodworker standing at the bench properly.
    const bench = this.game.entities
      .tryGetSingleton(BenchDive)
      ?.openBench()
      ?.view();
    if (bench) {
      const stance = workStance(bench);
      if (stance) {
        standAtStance(this.game, stance);
      } else {
        setMoveInput(this.game, [0, 0]);
      }
      return;
    }
    const [x, y] = this.game.io.getMovementVector();
    // An open trip card is using W/S for its row cursor; only A/D drive
    // the body (the old shell's `captureVertical`).
    const captureVertical =
      this.game.entities.tryGetSingleton(TargetingState)?.truckMenuOpen ??
      false;
    setMoveInput(this.game, [x, captureVertical ? 0 : y]);
  }
}
